import axios from 'axios'
import { wrapApiError } from '@/utils/apiError'

const API_URL = process.env.VUE_APP_API_URL || 'http://127.0.0.1:8000'

export const subscriptionApi = {
  async getSubscriptionPlans() {
    try {
      const response = await axios.get(`${API_URL}/subscriptions/plans`);
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to fetch subscription plans', 'No response from server');
    }
  },

  async getUsage() {
    // Usage data is now consolidated with limits in /subscriptions/limits
    // This method is kept for backward compatibility
    return this.getLimits();
  },

  async getUserSubscription() {
    try {
      const response = await axios.get(`${API_URL}/subscriptions/my-subscription`);
      return response.data;
    } catch (error) {
      // 404 = no subscription — not an error condition
      if (error.response?.status === 404) {
        return null;
      }
      throw wrapApiError(error, 'Failed to fetch user subscription', 'No response from server - please check your connection');
    }
  },

  async getPaymentProviderDetails() {
    try {
      const response = await axios.get(`${API_URL}/subscriptions/payment-provider-details`);
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to get payment provider details', 'No response from server');
    }
  },
  
  async createCheckoutSession(planId) {
    try {
      const response = await axios.post(`${API_URL}/subscriptions/create-checkout`, {
        plan_id: planId
      });
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to create checkout session', 'No response from server');
    }
  },

  // DEPRECATED: createDirectSubscription removed - use upgradeTrial instead
  // This function called the deprecated /subscriptions/create-direct endpoint
  // which was part of the old Sumit-managed subscription system.
  // All subscription creation now goes through the trial upgrade flow.

  async reactivateSubscription() {
    try {
      const response = await axios.post(`${API_URL}/subscriptions/reactivate`);
      return response.data;
    } catch (error) {
      // 404 = no canceled subscription to reactivate
      if (error.response?.status === 404) {
        throw { ...wrapApiError(error, 'No canceled subscription found to reactivate.'), type: 'no_canceled_subscription' };
      }
      throw wrapApiError(error, 'Failed to reactivate subscription', 'No response from server');
    }
  },

  async changePlan(planId) {
    try {
      const response = await axios.post(`${API_URL}/subscriptions/change-plan`, {
        plan_id: planId
      });
      return response.data;
    } catch (error) {
      // 404 = no active subscription to change
      if (error.response?.status === 404) {
        throw { ...wrapApiError(error, 'No active subscription found. You need an active subscription to change plans.'), type: 'no_subscription' };
      }
      throw wrapApiError(error, 'Failed to change subscription plan', 'No response from server - please check your connection');
    }
  },

  async getCustomerPortal() {
    try {
      const response = await axios.get(`${API_URL}/subscriptions/customer-portal`);
      return response.data;
    } catch (error) {
      // 404 = portal requires an active subscription
      if (error.response?.status === 404) {
        throw { ...wrapApiError(error, 'No active subscription found. You need an active subscription to access the customer portal.'), type: 'no_subscription' };
      }
      throw wrapApiError(error, 'Failed to get customer portal', 'No response from server - please check your connection');
    }
  },

  async listPaymentMethods() {
    try {
      const response = await axios.get(`${API_URL}/subscriptions/payment-methods`);
      return response.data.payment_methods || [];
    } catch (error) {
      throw wrapApiError(error, 'Failed to list payment methods', 'No response from server');
    }
  },

  async deletePaymentMethod(paymentMethodId) {
    try {
      const response = await axios.delete(`${API_URL}/subscriptions/payment-methods/${paymentMethodId}`);
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to delete payment method', 'No response from server');
    }
  },

  async cancelSubscription() {
    try {
      const response = await axios.post(`${API_URL}/subscriptions/cancel`);
      return response.data;
    } catch (error) {
      // 404 = no subscription to cancel
      if (error.response?.status === 404) {
        throw { ...wrapApiError(error, 'No active subscription found to cancel.'), type: 'no_subscription' };
      }
      throw wrapApiError(error, 'Failed to cancel subscription', 'No response from server - please check your connection');
    }
  },

  async getSubscriptionSuccess() {
    try {
      const response = await axios.get(`${API_URL}/subscriptions/success`);
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to confirm subscription success', 'No response from server - please check your connection');
    }
  },

  async getPaymentHistory(params = {}) {
    try {
      const response = await axios.get(`${API_URL}/payment/history`, { 
        params: {
          limit: params.limit || 10,
          offset: params.offset || 0
        } 
      });
      return response.data.transactions;
    } catch (error) {
      // 404 = no payment history — empty result for users without subscriptions
      if (error.response?.status === 404) {
        return [];
      }
      throw wrapApiError(error, 'Failed to fetch payment history', 'No response from server - please check your connection');
    }
  },

  async getPaymentStatistics() {
    try {
      const response = await axios.get(`${API_URL}/payment/statistics`);
      return response.data;
    } catch (error) {
      // 404 = no payment statistics — zero stats for users without subscriptions
      if (error.response?.status === 404) {
        return {
          total_paid: 0,
          total_transactions: 0,
          first_payment_date: null,
          last_payment_date: null,
          currency: 'USD'
        };
      }
      throw wrapApiError(error, 'Failed to fetch payment statistics', 'No response from server - please check your connection');
    }
  },

  async getTransactionDetails(transactionId) {
    try {
      const response = await axios.get(`${API_URL}/payment/${transactionId}`);
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to fetch transaction details', 'No response from server');
    }
  },

  async getLimits() {
    try {
      const response = await axios.get(`${API_URL}/subscriptions/limits`);
      
      /*
       * Expected response structure (new unified format):
       * {
       *   usage: { product_import: number, message_generation: number }, // backward compatibility
       *   limits: { max_product_imports_per_month: number, max_message_generations_per_month: number }, // backward compatibility
       *   resource_limits: { communities_used: number, max_communities: number, ... },
       *   monthly_limits: { message_generations_used: number, max_message_generations_per_month: number, ... },
       *   daily_limits: { messages_sent: number, max_messages_per_day: number, ... },
       *   feature_access: { message_generation: boolean, custom_ai_instructions: boolean, ... },
       *   reset_dates: { monthly_reset: string, daily_reset: string }
       * }
       */
      
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to fetch subscription limits', 'Error fetching subscription limits');
    }
  },

  // DEPRECATED: createSubscription removed - use upgradeTrial instead
  // This function called the deprecated /payment/create-subscription endpoint
  // which was part of the old Sumit-managed subscription system.
  // All subscription creation now goes through the trial upgrade flow.

  // Self-Managed Subscription API Methods

  // async getSubscriptionStatus() {
  //   try {
  //     const response = await axios.get(`${API_URL}/subscriptions/subscription-status`);
  //     return response.data;
  //   } catch (error) {
  //     if (error.response) {
  //       throw {
  //         message: error.response.data.detail || 'Failed to get subscription status',
  //         status: error.response.status,
  //         details: error.response.data
  //       };
  //     } else if (error.request) {
  //       throw {
  //         message: 'No response from server',
  //         status: 0,
  //         details: error.request
  //       };
  //     } else {
  //       throw {
  //         message: 'Error getting subscription status',
  //         status: 0,
  //         details: error.message
  //       };
  //     }
  //   }
  // },

  // async storePaymentMethod(token) {
  //   try {
  //     const response = await axios.post(`${API_URL}/subscriptions/store-payment-method`, {
  //       token: token
  //     });
  //     return response.data;
  //   } catch (error) {
  //     if (error.response) {
  //       throw {
  //         message: error.response.data.detail || 'Failed to store payment method',
  //         status: error.response.status,
  //         details: error.response.data
  //       };
  //     } else if (error.request) {
  //       throw {
  //         message: 'No response from server',
  //         status: 0,
  //         details: error.request
  //       };
  //     } else {
  //       throw {
  //         message: 'Error storing payment method',
  //         status: 0,
  //         details: error.message
  //       };
  //     }
  //   }
  // },

  // async upgradeTrial(planId, token) {
  //   try {
  //     const response = await axios.post(`${API_URL}/subscriptions/upgrade-trial`, {
  //       plan_id: planId,
  //       token: token
  //     });
  //     return response.data;
  //   } catch (error) {
  //     if (error.response) {
  //       throw {
  //         message: error.response.data.detail || 'Failed to upgrade trial',
  //         status: error.response.status,
  //         details: error.response.data
  //       };
  //     } else if (error.request) {
  //       throw {
  //         message: 'No response from server',
  //         status: 0,
  //         details: error.request
  //       };
  //     } else {
  //       throw {
  //         message: 'Error upgrading trial',
  //         status: 0,
  //         details: error.message
  //       };
  //     }
  //   }
  // },

  async changePlanProrated(planId) {
    try {
      const response = await axios.post(`${API_URL}/subscriptions/change-plan-prorated`, {
        plan_id: planId
      });
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to change plan', 'No response from server');
    }
  },

  async getBillingHistory() {
    try {
      const response = await axios.get(`${API_URL}/subscriptions/billing-history`);
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to get billing history', 'No response from server');
    }
  },

  async storePaymentMethod({ token }) {
    try {
      const response = await axios.post(`${API_URL}/subscriptions/store-payment-method`, {
        token: token
      });
      return response.data;
    } catch (error) {
      // 402 = payment method stored but the charge itself failed
      if (error.response?.status === 402) {
        const detail = error.response.data?.detail || {};
        throw {
          ...wrapApiError(error, 'Payment method stored but billing failed'),
          payment_method_stored: detail.payment_method_stored || false,
          billing_retry: detail.billing_retry || null
        };
      }
      throw wrapApiError(error, 'Failed to store payment method', 'No response from server');
    }
  },

  async upgradeTrial({ plan_id, token, currency }) {
    try {
      const response = await axios.post(`${API_URL}/subscriptions/upgrade-trial`, {
        plan_id: plan_id,
        token: token,
        currency: currency || 'USD'  // Default to USD if not provided
      });
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to upgrade trial', 'No response from server');
    }
  },

  async getSubscriptionStatus() {
    try {
      const response = await axios.get(`${API_URL}/subscriptions/subscription-status`);
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to get subscription status', 'No response from server');
    }
  },

  async verifyPaymentMethod() {
    try {
      const response = await axios.get(`${API_URL}/subscriptions/verify-payment-method`);
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to verify payment method', 'No response from server');
    }
  },

  async updateTrialPlan(planId) {
    try {
      const response = await axios.put(`${API_URL}/subscriptions/update-trial-plan`, {
        plan_id: planId
      });
      return response.data;
    } catch (error) {
      throw wrapApiError(error, 'Failed to update trial plan', 'No response from server');
    }
  },
}

export default subscriptionApi 